import { ReactNode, useMemo, useState } from "react"
import { useIntl } from "react-intl"

import { StaffLabelTextS } from "@doktor-se/bones-ui"
import { Loader } from "@doktor-se/bones-ui/dist/web-shared/components"

import { showAdditionalColumn } from "lib/conversations"

import EmptyListRow from "./EmptyListRow"
import ListHeader from "./ListHeader"
import ListRow from "./ListRow"

export interface TableColumn {
  key: string
  name: string
}

export interface ConversationListItem<T = any> {
  conversation: T
  values: Record<string, ReactNode>
  // Raw values used for sorting. If omitted for a column, falls back to string content when possible.
  sortValues?: Record<string, string | number | boolean | null | undefined>
}

interface ConversationTableProps<T = any> {
  title?: string
  titleStyle?: React.CSSProperties
  columns: TableColumn[]
  items: ConversationListItem<T>[]
  emptyMessage?: string
  onItemClick?: (item: ConversationListItem<T>) => void
  shouldNotify?: (item: ConversationListItem<T>) => boolean
  loading?: boolean
  showHeader?: boolean
  additionalColumn?: boolean
}

const ConversationTable = <T = any,>({
  title,
  titleStyle = { marginBottom: "16px" },
  columns,
  items,
  emptyMessage,
  onItemClick,
  shouldNotify,
  loading = false,
  showHeader = true,
  additionalColumn
}: ConversationTableProps<T>) => {
  const intl = useIntl()
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc")

  const defaultEmptyMessage = intl.formatMessage({ id: "label.overview.list_empty" })
  const displayEmptyMessage = emptyMessage || defaultEmptyMessage

  // Auto-detect additional column if not explicitly provided
  const hasAdditionalColumn = additionalColumn !== undefined ? additionalColumn : showAdditionalColumn(items)

  const onHeaderClick = (key: string) => {
    if (sortKey === key) {
      setSortDir(prev => (prev === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDir("asc")
    }
  }

  const sortedItems = useMemo(() => {
    if (!sortKey) return items

    const getComparable = (item: ConversationListItem) => {
      const raw = item.sortValues?.[sortKey]
      if (raw != null) {
        return typeof raw === "string" ? raw.toLowerCase() : typeof raw === "boolean" ? (raw ? 1 : 0) : raw
      }
      const fallback = item.values[sortKey]
      return typeof fallback === "string" ? fallback.toLowerCase() : ""
    }

    const direction = sortDir === "asc" ? 1 : -1
    return [...items].sort((a, b) => {
      const va = getComparable(a)
      const vb = getComparable(b)
      return va < vb ? -direction : va > vb ? direction : 0
    })
  }, [items, sortDir, sortKey])

  if (loading) {
    return (
      <div style={{ position: "relative", minHeight: "100px" }}>
        <Loader type="small" />
      </div>
    )
  }

  return (
    <>
      {title && <StaffLabelTextS style={titleStyle}>{title}</StaffLabelTextS>}

      {showHeader && (
        <ListHeader
          columns={columns}
          additionalColumn={hasAdditionalColumn}
          onHeaderClick={onHeaderClick}
          sortKey={sortKey || undefined}
          sortDir={sortDir}
        />
      )}

      {sortedItems.length ? (
        sortedItems.map((item, index) => {
          // Convert values object to ordered array based on column keys
          const orderedColumns = columns.map(col => ({
            key: col.key,
            content: item.values[col.key] as string | JSX.Element | undefined
          }))

          return (
            <ListRow
              key={(item.conversation as any)?.id || index}
              column={orderedColumns}
              onClick={onItemClick ? () => onItemClick(item) : undefined}
              notify={shouldNotify ? shouldNotify(item) : false}
              conversationId={(item.conversation as any)?.id}
            />
          )
        })
      ) : (
        <EmptyListRow>{displayEmptyMessage}</EmptyListRow>
      )}
    </>
  )
}

export default ConversationTable
